import { useQuery } from "@tanstack/react-query";
import { ProgrammeSummaryMetric } from "../types";
import { programmeDashboardApi } from "../api/programmeDashboard";
import { formatCurrency } from "../../../shared/utils";
import { FilterItem } from "../../../shared/context/FilterContext";
import { extractFilterValues } from "../../../lib/filterUtils";

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export const useProgrammeSummary = ({ portfolio_id, quickFilters }: { portfolio_id: string | undefined, quickFilters: any }) => {

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["programme-summary", portfolio_id, quickFilters],
    queryFn: () => {
      const params = {
        portfolio_id: portfolio_id,
        workstream_id: extractFilterValues(quickFilters.workstream) || null,
        site_id: extractFilterValues(quickFilters.site) || null,
        programme_id: extractFilterValues(quickFilters.programme) || null,
        region_id: extractFilterValues(quickFilters.region) || null,
        work_type_group_id: extractFilterValues(quickFilters['worktype-group']) || null,
        project_year: extractFilterValues(quickFilters.projectYear) || null,
      };
      return programmeDashboardApi.fetchProgrammeSummary(params);
    }
  });

  const metrics: ProgrammeSummaryMetric[] = data?.data
    ? [
      {
        label: "Total Budget",
        value: formatCurrency(data.data.total_budget),
      },
      {
        label: "Spend to Date",
        value: formatCurrency(data.data.spend_to_date),
        subValue: `${formatPercentage(data.data.spend_percent)} of budget`,
      },
      {
        label: "Latest Forecast (inc variance)",
        value: formatCurrency(data.data.latest_forecast),
        subValue: `${data.data.variance >= 0 ? "+" : ""}${formatCurrency(
          data.data.variance
        )} variance`,
      },
      {
        label: "Number of Active Projects",
        value: data.data.active_projects,
      },
      {
        label: "Count of projects not started",
        value: data.data.not_started_projects,
      },
      {
        label: "Programme TCQI",
        value: formatPercentage(data.data.programme_tcqi),
      },
    ]
    : [];

  return {
    metrics,
    isLoading,
    isError,
    error:
      error instanceof Error
        ? error.message
        : "Failed to fetch portfolio summary",
  };
};
